import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import {
  APPLY_ALL,
  CHANGE_PAGE,
  CLEAR_ALL,
  REMOVE_BREADCRUMB,
  SELECT_PRODUCT_GROUP,
  SELECT_PRODUCT_GROUP_MULTI,
  SET_SELECTED_PRODUCT_GROUPS,
  SET_SHOW_RELEVANT_TOGGLE,
  TOGGLE_FACET,
  UPDATE_ASSISTANT_VALUES,
  UPDATE_CAMPAIGN_FILTER,
  UPDATE_RELEVANT_FILTER,
  UPDATE_SEARCH_PARAMS_FROM_URL,
  UPDATE_SEARCH_TEXT,
} from "../actionTypes";
import { type FromUrlSearchParams } from "../../utils/historyManipulation";
import { SearchPageType } from "~/features/search/models/SearchPageType";

export interface SearchParamsState {
  text: string;
  currentPage: number;
  showOnlyCampaignResults: boolean;
  showOnlyRelevant: boolean;
  showRelevantToggle: boolean;
}

export const searchParamsInitialState: SearchParamsState = {
  text: "",
  currentPage: 0,
  showOnlyCampaignResults: false,
  showOnlyRelevant: false,
  showRelevantToggle: false,
};

export const searchParamsSlice = createSlice({
  name: "searchParams",
  initialState: searchParamsInitialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .addCase(UPDATE_SEARCH_PARAMS_FROM_URL, (state, action: PayloadAction<FromUrlSearchParams> | any) => {
        const params = action.payload;
        state.text = params.text || "";
        state.currentPage = params.currentPage ? +params.currentPage : 0;
      })
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .addCase(CHANGE_PAGE, (state, action: PayloadAction<number> | any) => {
        state.currentPage = action.payload;
      })
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .addCase(UPDATE_CAMPAIGN_FILTER, (state, action: PayloadAction<boolean> | any) => {
        state.showOnlyCampaignResults = action.payload;
      })
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .addCase(UPDATE_RELEVANT_FILTER, (state, action: PayloadAction<boolean> | any) => {
        state.showOnlyRelevant = action.payload;
      })
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .addCase(SET_SHOW_RELEVANT_TOGGLE, (state, action: PayloadAction<boolean> | any) => {
        state.showRelevantToggle = action.payload;
      })
      .addCase(
        UPDATE_SEARCH_TEXT,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (state, action: PayloadAction<{ searchText: string; searchPageType: SearchPageType }> | any) => {
          const { searchText, searchPageType } = action.payload;
          const clearSalesCampaignFilter = searchPageType !== SearchPageType.Campaign;
          state.text = searchText;
          state.currentPage = 0;
          if (clearSalesCampaignFilter) {
            state.showOnlyCampaignResults = false;
          }
        },
      )
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .addCase(CLEAR_ALL, (state, action: PayloadAction<{ searchPageType: SearchPageType }> | any) => {
        const { searchPageType } = action.payload;
        const isCampaignPage = searchPageType === SearchPageType.Campaign;
        state.currentPage = 0;
        if (!isCampaignPage) {
          state.text = "";
          state.showOnlyCampaignResults = false;
        }
      })
      .addCase(TOGGLE_FACET, (state) => {
        state.currentPage = 0;
      })
      .addCase(SELECT_PRODUCT_GROUP, (state) => {
        state.currentPage = 0;
      })
      .addCase(SELECT_PRODUCT_GROUP_MULTI, (state) => {
        state.currentPage = 0;
      })
      .addCase(SET_SELECTED_PRODUCT_GROUPS, (state) => {
        state.currentPage = 0;
      })
      .addCase(UPDATE_ASSISTANT_VALUES, (state) => {
        state.currentPage = 0;
      })
      .addCase(REMOVE_BREADCRUMB, (state) => {
        state.currentPage = 0;
      })
      .addCase(APPLY_ALL, (state) => {
        state.currentPage = 0;
      });
  },
});

export const searchParamsReducer = searchParamsSlice.reducer;
